import { type FC, type ReactNode, useEffect } from "react";
import {
  type Layout,
  type LayoutChangedMeta,
  useDefaultLayout,
  useGroupRef,
  usePanelRef
} from "react-resizable-panels";

import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup
} from "@/components/ui/resizable";
import { cn } from "@/lib/utils";

import { useOptionalDensity } from "../../density";

import { IDE_PANELS, ideLayoutStore, useIdeLayout } from "./ideLayoutStore";

const ROOT_PANELS = [IDE_PANELS.left, IDE_PANELS.center, IDE_PANELS.right];
const LEFT_PANELS = [IDE_PANELS.files, IDE_PANELS.workspace];
/** A collapsed explorer section keeps its 36px `PaneHeader`. */
const SECTION_HEADER_PX = 36;
/** Narrowest explorer whose Case Files header still shows its title and actions. */
const EXPLORER_MIN_PX = 280;
/** Comfortable pads the explorer's cards by 12px a side (styles/explorer-cards.css). */
const EXPLORER_CARD_INSET_PX = 24;

/** Persist a group's layout, except the one a focus mode laid out. */
const unlessFocused =
  (save: (layout: Layout, meta: LayoutChangedMeta) => void) =>
  (layout: Layout, meta: LayoutChangedMeta) => {
    if (!ideLayoutStore.isFocused()) save(layout, meta);
  };

type Props = {
  /** Top of the explorer, kept in every focus mode. */
  explorerHeader: ReactNode;
  /** The rows under it (Key Facts to USCIS), hidden while a section is maximized. */
  explorerRows: ReactNode;
  files: ReactNode;
  workspace: ReactNode;
  /** Wraps the files / workspace split (the explorer's drag and drop). */
  wrapSections?: (sections: ReactNode) => ReactNode;
  center: ReactNode;
  right: ReactNode;
};

/**
 * The IDE's resizable skeleton: the explorer (with its files / workspace
 * split) on the left, the document in the center, Casey on the right. Sizes
 * persist per group in localStorage via react-resizable-panels'
 * `useDefaultLayout`; the side panels and both sections collapse, and every
 * group and panel is registered in `ideLayoutStore` for the toggles and the
 * focus modes.
 */
export const IdeLayout: FC<Props> = ({
  explorerHeader,
  explorerRows,
  files,
  workspace,
  wrapSections = (sections) => sections,
  center,
  right
}) => {
  const root = useDefaultLayout({ id: "caseIde.root", panelIds: ROOT_PANELS });
  const left = useDefaultLayout({ id: "caseIde.left", panelIds: LEFT_PANELS });
  const rootGroup = useGroupRef();
  const explorerGroup = useGroupRef();
  const leftPanel = usePanelRef();
  const rightPanel = usePanelRef();
  const filesPanel = usePanelRef();
  const workspacePanel = usePanelRef();
  const { focus } = useIdeLayout();
  const comfortable =
    (useOptionalDensity()?.density ?? "comfortable") === "comfortable";
  const explorerMinPx =
    EXPLORER_MIN_PX + (comfortable ? EXPLORER_CARD_INSET_PX : 0);
  const sectionFocus = focus === "files" || focus === "workspace";

  useEffect(() => {
    ideLayoutStore.registerGroup("root", rootGroup.current);
    ideLayoutStore.registerGroup("explorer", explorerGroup.current);
    ideLayoutStore.registerSection("files", filesPanel.current);
    ideLayoutStore.registerSection("workspace", workspacePanel.current);
    return () => {
      ideLayoutStore.registerGroup("root", null);
      ideLayoutStore.registerGroup("explorer", null);
      ideLayoutStore.registerSection("files", null);
      ideLayoutStore.registerSection("workspace", null);
    };
  }, [rootGroup, explorerGroup, filesPanel, workspacePanel]);
  useEffect(() => {
    ideLayoutStore.register("left", leftPanel.current);
    return () => {
      ideLayoutStore.register("left", null);
    };
  }, [leftPanel]);
  useEffect(() => {
    ideLayoutStore.register("right", rightPanel.current);
    return () => {
      ideLayoutStore.register("right", null);
    };
  }, [rightPanel]);

  return (
    <ResizablePanelGroup
      orientation="horizontal"
      groupRef={rootGroup}
      defaultLayout={root.defaultLayout}
      onLayoutChanged={unlessFocused(root.onLayoutChanged)}
      // Maximized Casey takes the whole row. The other panes stay mounted
      // (hidden), so restoring is instant and keeps their state.
      className={cn(
        "min-h-0 flex-1",
        focus === "casey" &&
          "[&>[data-panel]:not(#ide-right)]:hidden! [&>[data-separator]]:hidden!"
      )}
      data-casey-full={focus === "casey" ? "" : undefined}
      data-focus={focus ?? undefined}
    >
      <ResizablePanel
        id={IDE_PANELS.left}
        panelRef={leftPanel}
        collapsible
        collapsedSize={0}
        defaultSize="22"
        minSize={explorerMinPx}
        maxSize="70"
        onResize={() => ideLayoutStore.sync()}
      >
        <div className="explorer-stack flex h-full min-h-0 flex-col">
          {/* A card in Comfortable (styles/explorer-cards.css); transparent
              (`contents`) in Compact. */}
          <div className="explorer-client-card contents">
            {explorerHeader}
            <div className={sectionFocus ? "hidden" : "contents"}>
              {explorerRows}
            </div>
          </div>
          {wrapSections(
            <ResizablePanelGroup
              orientation="vertical"
              groupRef={explorerGroup}
              defaultLayout={left.defaultLayout}
              onLayoutChanged={unlessFocused(left.onLayoutChanged)}
              className="explorer-sections min-h-0 flex-1"
            >
              <ResizablePanel
                id={IDE_PANELS.files}
                panelRef={filesPanel}
                collapsible
                collapsedSize={SECTION_HEADER_PX}
                defaultSize="55"
                minSize="20"
              >
                {files}
              </ResizablePanel>
              <ResizableHandle />
              <ResizablePanel
                id={IDE_PANELS.workspace}
                panelRef={workspacePanel}
                collapsible
                collapsedSize={SECTION_HEADER_PX}
                defaultSize="45"
                minSize="20"
              >
                {workspace}
              </ResizablePanel>
            </ResizablePanelGroup>
          )}
        </div>
      </ResizablePanel>
      <ResizableHandle />
      <ResizablePanel id={IDE_PANELS.center} defaultSize="50" minSize="30">
        {center}
      </ResizablePanel>
      <ResizableHandle />
      <ResizablePanel
        id={IDE_PANELS.right}
        panelRef={rightPanel}
        collapsible
        collapsedSize={0}
        defaultSize="28"
        minSize="20"
        maxSize="70"
        onResize={() => ideLayoutStore.sync()}
      >
        {right}
      </ResizablePanel>
    </ResizablePanelGroup>
  );
};
